/**
 * Zentrales Glossar (backlog.md -> C14) fuer Nicht-KPI-Konzepte, die an keiner
 * bestehenden `KpiDefinition` haengen und daher kein eigenes `tooltip`-Feld in
 * `lib/kpi-config.ts` haben. Bewusst additiv: die dort bereits bestehenden KPI-Tooltips
 * (KGV, ROIC, Nettomarge, ...) bleiben unangetastet, dieses Glossar deckt nur die echte
 * Luecke ab (Fair Value, CAGR, Vorabpauschale, ...) - "das risikoaermere Vorgehen" laut
 * Backlog-Eintrag, keine Konsolidierung/Umbau des bestehenden Systems.
 */

export interface GlossaryEntry {
  term: string;
  definition: string;
}

export const GLOSSARY = {
  fairValue: {
    term: "Fair Value",
    definition:
      "Rechnerischer Schätzwert einer Aktie, hier abgeleitet aus Gewinn je Aktie × durchschnittlichem historischem KGV dieser Aktie - kein Kursziel, keine Empfehlung, nur eine Bewertungsreferenz auf Basis der eigenen Historie.",
  },
  cagr: {
    term: "CAGR",
    definition:
      "Compound Annual Growth Rate - die jährliche Wachstumsrate, die über mehrere Jahre hinweg denselben Gesamteffekt ergäbe wie die tatsächliche, meist unregelmäßige Entwicklung.",
  },
  xirr: {
    term: "XIRR",
    definition:
      "Geldgewichtete (nicht zeitgewichtete) persönliche Rendite - berücksichtigt Zeitpunkt und Höhe jedes einzelnen Kaufs/Verkaufs, nicht nur Start- und Endwert.",
  },
  moat: {
    term: "Burggraben (Moat)",
    definition:
      "Dauerhafter Wettbewerbsvorteil, der ein Unternehmen vor Konkurrenz schützt - z. B. eine starke Marke, Netzwerkeffekte, hohe Wechselkosten für Kunden oder Skalenvorteile.",
  },
  reverseDcf: {
    term: "Reverse-DCF",
    definition:
      "Discounted-Cashflow-Modell rückwärts gerechnet: aus dem heutigen Kurs wird abgeleitet, welches Wachstum der Markt implizit einpreist - statt aus einer angenommenen Wachstumsrate einen fairen Wert zu berechnen.",
  },
  piotroskiFScore: {
    term: "Piotroski F-Score",
    definition:
      "Punktzahl von 0-9 aus neun Rentabilitäts-, Verschuldungs- und Effizienzkriterien - je höher, desto mehr fundamentale Qualitätsmerkmale sind laut dieser Methodik erfüllt.",
  },
  altmanZScore: {
    term: "Altman Z-Score",
    definition:
      "Kennzahl zur statistischen Einschätzung des Insolvenzrisikos aus fünf Bilanz-/Ertragskennzahlen - ursprünglich für börsennotierte Industrieunternehmen entwickelt.",
  },
  beneishMScore: {
    term: "Beneish M-Score",
    definition:
      "Statistisches Modell, das anhand mehrerer Bilanzkennzahlen abschätzt, wie wahrscheinlich eine Ergebnismanipulation in den Geschäftszahlen ist.",
  },
  vorabpauschale: {
    term: "Vorabpauschale",
    definition:
      "Jährliche deutsche Besteuerung thesaurierender Fonds/ETFs auch ohne tatsächliche Ausschüttung (§ 18 InvStG) - berechnet aus Fondswert, Basiszins und der tatsächlichen Wertsteigerung im Jahr.",
  },
  basiszins: {
    term: "Basiszins",
    definition:
      "Jährlich vom Bundesfinanzministerium veröffentlichter Zinssatz, abgeleitet aus der Rendite öffentlicher Anleihen - Grundlage für die Berechnung der Vorabpauschale.",
  },
  teilfreistellung: {
    term: "Teilfreistellung",
    definition:
      "Gesetzlich festgelegter Anteil der Fondserträge, der von der Vorabpauschale/Kapitalertragsteuer ausgenommen ist - abhängig vom Fondstyp (z. B. 30 % bei Aktienfonds).",
  },
  drawdown: {
    term: "Drawdown",
    definition:
      "Prozentualer Rückgang vom letzten Höchststand zum jeweiligen Tiefpunkt - ein Maß für den maximalen zwischenzeitlichen Wertverlust.",
  },
  totalReturn: {
    term: "Total Return",
    definition:
      "Gesamtrendite inklusive reinvestierter Dividenden/Ausschüttungen - im Gegensatz zur reinen Kursrendite, die Ausschüttungen unberücksichtigt lässt.",
  },
  peerGroup: {
    term: "Peer-Group",
    definition:
      "Gruppe vergleichbarer Unternehmen (ähnliche Branche, Geschäftsmodell und Größe), die zur Einordnung von Bewertung und Kennzahlen herangezogen wird.",
  },
  erikonomieScore: {
    term: "Erikonomie-Score",
    definition:
      "Automatisiert berechneter 0-100-Score aus acht fundamentalen Kennzahlen (Profitabilität, Wachstum, Verschuldung, Bewertung) - Bildungswerkzeug, keine Anlageberatung.",
  },
} as const satisfies Record<string, GlossaryEntry>;

export type GlossaryTermKey = keyof typeof GLOSSARY;
